<script lang="ts" setup>
  import { onBeforeUnmount, onMounted, watch } from 'vue'

  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui'
  import { useVModel } from '@vueuse/core'

  const props = withDefaults(
    defineProps<{
      active?: boolean
      closeable?: boolean
      secondCurtain?: boolean
      dense?: boolean
      showShadow?: boolean

      curtainClasses?: string
      curtainContentsClasses?: string
      closeBtnClasses?: string
    }>(),
    {
      active: false,
      closeable: true,
      secondCurtain: false,
      dense: false,
      showShadow: true,

      curtainClasses: 'w-auto min-w-[50%]',
      curtainContentsClasses: '',
      closeBtnClasses: 'top-5 right-5',
    }
  )
  const emit = defineEmits<{ (e: 'update:active', value: boolean): void }>()
  const activeModel = useVModel(props, 'active', emit)

  const { overflowHidden } = storeToRefs(useUiStore())
  watch(
    () => props.active,
    (newValue) => {
      if (!props.secondCurtain && newValue !== overflowHidden.value) {
        overflowHidden.value = newValue
      }
    }
  )

  onMounted(() => {
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && props.closeable) close()
    })
  })

  function close() {
    if (props.closeable) activeModel.value = false
  }

  onBeforeUnmount(() => {
    if (overflowHidden.value) overflowHidden.value = false
  })
</script>

<template>
  <transition name="slide-fade">
    <!-- Window -->
    <div
      v-if="activeModel"
      class="curtain window bg-white fixed top-0 right-0 max-w-[100%] h-full max-h-full z-40"
      :class="[
        props.curtainClasses,
        { 'curtain--active': activeModel },
        { 'second-curtain !z-50': props.secondCurtain },
        { dense: props.dense },
        { 'shadow-2xl': showShadow },
      ]"
    >
      <!-- Close Button -->
      <button
        v-if="props.closeable"
        aria-label="close"
        class="absolute cursor-pointer z-10"
        :class="props.closeBtnClasses"
        @click.prevent="close"
      >
        <slot name="close-btn-icon">
          <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 22L22 14M22 22L14 14"
              stroke="#1F1F1F"
              stroke-width="1.2"
              stroke-linecap="square"
            />
            <rect
              x="0.5"
              y="0.5"
              width="35"
              height="35"
              rx="17.5"
              stroke="black"
            />
          </svg>
        </slot>
      </button>

      <!-- Contents -->
      <div
        class="flex flex-col h-full max-h-full text-start"
        :class="props.curtainContentsClasses"
      >
        <slot />
      </div>
    </div>
  </transition>

  <transition name="fade">
    <!-- Shade -->
    <div
      v-if="activeModel"
      class="bg-black/20 fixed top-0 left-0 w-full h-full z-30"
      :class="[{ '!z-40': props.secondCurtain }]"
      @click.self="close"
    />
  </transition>
</template>

<style scoped>
  /* Transitions */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s linear;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.25s ease;
  }
  .slide-fade-enter-from,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateX(100%);
  }

  /* Styles */
  :slotted(h1),
  :slotted(header) {
    font-size: theme('fontSize.xl');
    line-height: theme('fontSize.xl');
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :slotted(h1) {
    max-height: 108px;
    padding: 2.5rem 4.75rem 2.5rem 2rem;
  }

  .dense :slotted(h1) {
    padding: 1.5rem 2rem;
  }

  :slotted(.curtain-header) {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    flex-grow: 0;
  }

  :slotted(.curtain-contents) {
    display: flex;
    flex-direction: column;
    overflow: auto;
    flex-shrink: 1;
    flex-grow: 1;
  }

  .second-curtain {
    min-width: calc(50% - 32px) !important;
  }
</style>
